import { inject, Injectable } from '@angular/core';
import { ToastsService } from '@catch/services';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { forkJoin, of } from 'rxjs';
import {
  catchError,
  concatMap,
  map,
  mergeMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { UsersAdapter } from '../../adapters/users.adapter';
import {
  createUser,
  createUserSuccess,
  deleteUser,
  deleteUserFailure,
  deleteUserSuccess,
  loadUserPermissions,
  loadUserPermissionsFailure,
  loadUserPermissionsSuccess,
  loadUsers,
  loadUsersCached,
  loadUsersFailure,
  loadUsersSuccess,
  resetUserTFA,
  resetUserTFAFailure,
  resetUserTFASuccess,
  updateUser,
  updateUserFailure,
  updateUserSuccess,
} from './users.actions';
import {
  selectOtherManagersByStepByUserId,
  selectUsersEntities,
  selectUsersTotal,
} from './users.reducer';

@Injectable()
export class UsersEffects {
  private readonly actions$ = inject(Actions);
  private readonly store = inject(Store);
  private readonly usersAdapter = inject(UsersAdapter);
  private readonly toastsService = inject(ToastsService);

  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUsers),
      withLatestFrom(this.store.select(selectUsersTotal)),
      concatMap(([, total]) => {
        if (total > 0) {
          return of(loadUsersCached());
        }

        return this.usersAdapter.getUsers().pipe(
          map((users) => loadUsersSuccess({ users })),
          catchError((error) => of(loadUsersFailure({ error }))),
        );
      }),
    ),
  );

  loadUsersFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(loadUsersFailure),
        tap((action) => {
          // Todo: Add proper error handling. Maybe with a toasty?
          //alert(action.error.message);
        }),
      ),
    { dispatch: false },
  );

  /**
   * Loads step roles + other-managers map for the permissions modal.
   * Skips HTTP when both are already in the store (reopens after close).
   */
  loadUserPermissions$ = createEffect(() =>
    this.actions$.pipe(
      ofType(loadUserPermissions),
      withLatestFrom(
        this.store.select(selectUsersEntities),
        this.store.select(selectOtherManagersByStepByUserId),
      ),
      concatMap(([{ userId }, usersById, otherManagersByUserId]) => {
        const roles = usersById[userId]?.roles;
        const otherManagersByStep = otherManagersByUserId[userId];
        if (roles != null && otherManagersByStep != null) {
          return of(
            loadUserPermissionsSuccess({ userId, roles, otherManagersByStep }),
          );
        }

        return forkJoin({
          roles: this.usersAdapter.getUserRoles(userId),
          otherManagers: this.usersAdapter.getOtherManagersByStep(userId),
        }).pipe(
          map(({ roles, otherManagers }) =>
            loadUserPermissionsSuccess({
              userId,
              roles,
              otherManagersByStep: Object.fromEntries(
                otherManagers.map((row) => [row.stepId, row.hasManager]),
              ),
            }),
          ),
          catchError((error) => of(loadUserPermissionsFailure({ error }))),
        );
      }),
    ),
  );

  createUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(createUser),
      mergeMap((props) =>
        this.usersAdapter.createUser(props.user).pipe(
          map((response) =>
            createUserSuccess({ user: { ...props.user, id: response.id } }),
          ),
          catchError((error) => {
            return of(deleteUserFailure({ error }));
          }),
        ),
      ),
    ),
  );

  createUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(createUserSuccess),
        tap(() => {
          this.toastsService.success(
            'company-tool.views.settings.company.users.toasts.created',
          );
        }),
      ),
    { dispatch: false },
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(updateUser),
      mergeMap((props) =>
        this.usersAdapter.editUser(props.userId, props.role).pipe(
          map(() =>
            updateUserSuccess({ userId: props.userId, role: props.role }),
          ),
          catchError((error) => {
            return of(updateUserFailure({ error }));
          }),
        ),
      ),
    ),
  );

  updateUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(updateUserSuccess),
        tap(() => {
          this.toastsService.success(
            'company-tool.views.settings.company.users.toasts.updated',
          );
        }),
      ),
    { dispatch: false },
  );

  deleteUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(deleteUser),
      mergeMap((props) =>
        this.usersAdapter.deleteUser(props.user).pipe(
          map(() => deleteUserSuccess({ id: props.user.id })),
          catchError((error) => {
            return of(deleteUserFailure({ error }));
          }),
        ),
      ),
    ),
  );

  deleteUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(deleteUserSuccess),
        tap(() => {
          this.toastsService.success(
            'company-tool.views.settings.company.users.toasts.deleted',
          );
        }),
      ),
    { dispatch: false },
  );

  resetUserTFA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(resetUserTFA),
      mergeMap((props) =>
        this.usersAdapter.resetTFA(props.user.id).pipe(
          map(() =>
            resetUserTFASuccess({ user: { ...props.user, hasTFA: false } }),
          ),
          catchError((error) => {
            return of(resetUserTFAFailure({ error }));
          }),
        ),
      ),
    ),
  );

  resetUserTFASuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(resetUserTFASuccess),
        tap(() => {
          this.toastsService.success(
            'company-tool.views.settings.company.users.sections.users.tfa.reset-toast',
          );
        }),
      ),
    { dispatch: false },
  );
}
